import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { v, CleanLetterhead, CleanFooter, FieldRow, KeepTogether, Section, SectionNumberingProvider, SigningBlock, TemplateDocument, } from '../../../atoms/index.js';
import { expandPartyProjection, PartyBlock } from '../../../parties/index.js';
export const metadata = {
    sections: [
        { ref: 'hyresvard', title: 'Hyresvärd' },
        { ref: 'hyresgast', title: 'Hyresgäst' },
        { ref: 'avtalskoppling', title: 'Avtalskoppling' },
        {
            ref: 'underskrift',
            title: 'Underskrifter',
            visibleWhen: { include_signing: true },
        },
    ],
    templateKey: 'clean_tillagg_lokal',
    version: 'v1',
    category: 'Lokalhyra',
    // Descriptive (non-contract) picker/option label — the real document name
    // is the user's mandatory `document_title` override (titleFromValueKey).
    title: 'Anpassningsbart tilläggsavtal till lokalhyresavtal',
    shortTitle: 'Anpassningsbart tilläggsavtal (lokal)',
    titleFromValueKey: 'document_title',
    connectedOnly: true,
    formNumber: '',
    preamble: '',
    description: 'Ett tomt tilläggsavtal till ett lokalhyresavtal. Parter och fastighet ' +
        'ärvs från avtalet; resten skriver du själv som egna sektioner. Välj om ' +
        'tillägget ska signeras eller endast exporteras.',
    status: 'active',
    signingMode: 'scrive',
    signingOptional: true,
    connectedTo: ['lokalhyra'],
    signingPlacementAnchors: 'per-party-label',
    inheritFromParent: {
        parties: 'by-role-ordinal',
        property: true,
        values: [{ key: 'contract_number', fromParentKey: 'contract_number' }],
    },
    partySlots: [
        { id: 'lessor', label: 'Hyresvärd', role: 'lessor', ordinal: 0, required: true, signs: true },
        { id: 'lessee', label: 'Hyresgäst', role: 'lessee', ordinal: 0, required: true, signs: true },
    ],
    // Party projection (ADR-0065) — both parties are orgs on a lokal avtal.
    partyProjection: [
        {
            slot: 'lessor',
            section: 'hyresvard',
            idHint: 'org',
            fields: [
                { attr: 'legal_name', key: 'landlord_name', requiredAtFinal: true },
                { attr: 'id_number', key: 'landlord_org_number' },
            ],
        },
        {
            slot: 'lessee',
            section: 'hyresgast',
            idHint: 'org',
            fields: [
                { attr: 'legal_name', key: 'tenant_name', requiredAtFinal: true },
                { attr: 'id_number', key: 'tenant_org_number' },
            ],
        },
    ],
    propertySlots: [{ id: 'property', label: 'Fastighet', multi: false, required: true }],
    defaultCustomSections: [{ title: 'Innehåll' }],
};
export const variableSchema = [
    // — Dokument —
    {
        key: 'document_title',
        label: 'Dokumenttitel',
        type: 'text',
        section: 'Dokument',
        default: 'Tilläggsavtal till lokalhyresavtal',
        requiredAtFinal: true,
        help: 'Visas överst i dokumentet och blir avtalets namn.',
    },
    {
        key: 'include_signing',
        label: 'Inkludera signering',
        type: 'boolean',
        section: 'Dokument',
        default: true,
        help: 'Av: dokumentet exporteras utan underskrift. På: undertecknas av parterna.',
        scopes: { copy: 'manual' },
    },
    // — Parter (ärvs från avtalet) — generated from metadata.partyProjection (ADR-0065).
    ...expandPartyProjection(metadata),
    // — Avtalskoppling (ärvs från avtalet) —
    {
        key: 'contract_number',
        label: 'Hyreskontrakt nr',
        type: 'text',
        section: 'avtalskoppling',
        requiredAtFinal: true,
        help: 'Numret på det lokalhyresavtal som tillägget avser. Förifylls från avtalet.',
        scopes: { copy: 'never' },
    },
    {
        key: 'property_designation',
        label: 'Fastighetsbeteckning',
        type: 'text',
        section: 'avtalskoppling',
        help: 'Förifylls från hyresavtalets fastighet.',
        autofill: { kind: 'property', field: 'name' },
    },
    {
        key: 'property_municipality',
        label: 'Kommun',
        type: 'text',
        section: 'avtalskoppling',
        help: 'Förifylls från hyresavtalets fastighet.',
        autofill: { kind: 'property', field: 'kommun' },
    },
    {
        key: 'property_address',
        label: 'Lokalens adress',
        type: 'textarea',
        section: 'avtalskoppling',
        help: 'Förifylls från hyresavtalets fastighet.',
        autofill: { kind: 'property', field: 'address' },
    },
];
export const sampleValues = {
    document_title: 'Tilläggsavtal till lokalhyresavtal',
    include_signing: true,
    landlord_name: 'Fastighetsbolaget Söder AB',
    landlord_org_number: '556789-1234',
    tenant_name: 'Café Söder HB',
    tenant_org_number: '969712-5678',
    contract_number: 'L-2042',
    property_designation: 'Sandbacken 12',
    property_municipality: 'Stockholm',
    property_address: 'Götgatan 35, 116 21 Stockholm',
};
export function Letterhead({ values, } = {}) {
    const title = (values?.['document_title'] ?? '').trim() ||
        'Tilläggsavtal till lokalhyresavtal';
    return _jsx(CleanLetterhead, { title: title });
}
export function PageFooter() {
    return _jsx(CleanFooter, {});
}
export function Body({ values, mode, signatories, }) {
    return (_jsxs(SectionNumberingProvider, { sections: metadata.sections ?? [], values: values, mode: mode, children: [_jsxs("div", { className: "grid grid-cols-2 gap-6", children: [_jsx(Section, { sectionRef: "hyresvard", title: "Hyresv\u00E4rd", children: _jsx(PartyBlock, { metadata: metadata, slot: "lessor", values: values }) }), _jsx(Section, { sectionRef: "hyresgast", title: "Hyresg\u00E4st", children: _jsx(PartyBlock, { metadata: metadata, slot: "lessee", values: values }) })] }), _jsxs(Section, { sectionRef: "avtalskoppling", title: "Avtalskoppling", children: [_jsx(FieldRow, { label: "Hyreskontrakt nr", children: v(values.contract_number) }), _jsx(FieldRow, { label: "Fastighetsbeteckning", children: v(values.property_designation) }), _jsx(FieldRow, { label: "Kommun", children: v(values.property_municipality) }), _jsx(FieldRow, { label: "Lokalens adress", children: v(values.property_address) })] }), _jsx(KeepTogether, { children: _jsx(Section, { sectionRef: "underskrift", title: "Underskrifter", children: _jsx(SigningBlock, { parties: [
                            { label: 'Hyresvärd', role: 'lessor', ordinal: 0 },
                            { label: 'Hyresgäst', role: 'lessee', ordinal: 0 },
                        ], signatories: signatories }) }) })] }));
}
export function Component(props) {
    return (_jsx(TemplateDocument, { header: _jsx(Letterhead, { values: props.values }), footer: _jsx(PageFooter, {}), children: _jsx(Body, { ...props }) }));
}
//# sourceMappingURL=index.js.map